import React, { useState } from "react";
import { useFinance } from "../context/FinanceContext";
import { FaTrash, FaSearch, FaRegStickyNote, FaRedo } from "react-icons/fa";
import "./TransactionList.css";

function TransactionList() {
  const { wallets, selectedWalletId, setSelectedWalletId, deleteTransaction, repeatTransaction } = useFinance();

  // state initialization
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredType, setFilteredType] = useState("all");
  const [sortOrder, setSortOrder] = useState("dateDesc");
  const [repeatModalOpen, setRepeatModalOpen] = useState(false);
  const [repeatTransaction_, setRepeatTransaction_] = useState(null);
  const [repeatAmount, setRepeatAmount] = useState("");
  const [repeatRemark, setRepeatRemark] = useState("");

  // format date for display 
  const formatDate = (date) => {
    const d = new Date(date);

    if (isNaN(d.getTime())) {
    return "Invalid Date";
  }
    const dateStr = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    
    const timeStr = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${dateStr} ${timeStr}`;
  };

  // Handle repeat transaction
  const handleOpenRepeatModal = (transaction) => {
    setRepeatTransaction_(transaction);
    setRepeatAmount(transaction.amount.toString());
    setRepeatRemark(transaction.remark || "");
    setRepeatModalOpen(true);
  };

  const handleConfirmRepeat = () => {
    if (repeatTransaction_) {
      const updatedTransaction = {
        ...repeatTransaction_,
        remark: repeatRemark,
      };
      repeatTransaction(repeatTransaction_.walletId, updatedTransaction, repeatAmount);
      setRepeatModalOpen(false);
      setRepeatTransaction_(null);
      setRepeatAmount("");
      setRepeatRemark("");
      alert("Transaction repeated successfully!");
    }
  };

  const handleCancelRepeat = () => {
    setRepeatModalOpen(false);
    setRepeatTransaction_(null);
    setRepeatAmount("");
    setRepeatRemark("");
  };

  // Select wallet
  let displayedTransactions = [];

  if (selectedWalletId === "all") {
    wallets.forEach((wallet) => {
      wallet.transactions.forEach((transaction) => {
        displayedTransactions.push({
          ...transaction,
          walletName: wallet.name,
          walletId: wallet.id,
        });
      });
    });
  } else {
    const currentWallet = wallets.find(
      (wallet) => wallet.id === selectedWalletId
    );
    displayedTransactions = currentWallet?.transactions || [];
    displayedTransactions = displayedTransactions.map((tx) => ({
      ...tx,
      walletName: currentWallet.name,
      walletId: currentWallet.id,
    }));
  }

  // Search
  if (searchQuery.trim() !== "") {
    displayedTransactions = displayedTransactions.filter((transaction) =>
      transaction.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Filter
  if (filteredType !== "all") {
    displayedTransactions = displayedTransactions.filter(
      (transaction) => transaction.type === filteredType
    );
  }

  // Sort
  displayedTransactions.sort((a, b) => {
    if (sortOrder === "dateAsc") return new Date(a.date) - new Date(b.date);
    if (sortOrder === "dateDesc") return new Date(b.date) - new Date(a.date);
    if (sortOrder === "amountAsc") return a.amount - b.amount;
    if (sortOrder === "amountDesc") return b.amount - a.amount;
    return 0;
  });

  return (
    <div className="transaction-list-container">
      {/* Top Row: Title and Filters */}
      <div className="transaction-top-row">
        <h2>Transaction History</h2>

        <div className="transaction-top-filters">
          {/* Search Box */}
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="search"
              placeholder="Search category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              id="searchQuery"
              name="searchQuery"
            />
          </div>

          {/* Type Filter */}
          <select
            value={filteredType}
            onChange={(e) => setFilteredType(e.target.value)}
            className="transaction-select"
            id="filteredType"
            name="filteredType"
          >
            <option value="all">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>

          {/* Sort Order */}
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="transaction-select"
            id="sortOrder"
            name="sortOrder"
          >
            <option value="dateDesc">Date: Newest</option>
            <option value="dateAsc">Date: Oldest</option>
            <option value="amountDesc">Amount: High to Low</option>
            <option value="amountAsc">Amount: Low to High</option>
          </select>

          {/* Wallet Selector */}
          <select
            value={selectedWalletId}
            onChange={(e) =>
              e.target.value === "all"
                ? setSelectedWalletId("all")                  // keep "all" as string
                : setSelectedWalletId(Number(e.target.value)) // convert to number
            }
            className="transaction-select"
            id="selectedWalletId"
            name="selectedWalletId"
          >
            <option value="all">All Wallets</option>
            {wallets.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table Header */}
      <div className={`transaction-wrapper`}>
      <div
        className={`transaction-table-header ${
          selectedWalletId === "all" ? "with-wallet" : ""
        }`}
      >
        <span>Transaction ID</span>
        {selectedWalletId === "all" && <span>Wallet</span>}
        <span>Category</span>
        <span className="amount-header">Amount</span>
        <span className="type-header">Type</span>
        <span className="remark-header">Remark</span>
        <span className="date-header">Date</span>
        <span className="action-header">Actions</span>
      </div>

      {/* Table Body */}
      <div className="transaction-table-body">
        {displayedTransactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><FaRegStickyNote className="FaIcon"/></div>
            <h3>No transactions found</h3>
            <p>Try adjusting your filters or add a new transaction</p>
          </div>
        ) : (
          displayedTransactions.map((transaction) => (
            <div
              key={transaction.id}
              className={`transaction-row ${
                selectedWalletId === "all" ? "with-wallet" : ""
              }`}
            >
              <span className="transaction-id" title={`TXN-${transaction.id}`}>
                TXN-{transaction.id}
              </span>

              {selectedWalletId === "all" && (
                <span className="wallet-name">{transaction.walletName}</span>
              )}

              <span className="category-cell">{transaction.category}</span>

              <span className={`amount-cell ${transaction.type}`}>
                {transaction.type === "income" ? "+" : "-"}$
                {Math.abs(transaction.amount).toFixed(2)}
              </span>

              <span className="type-cell">
                <span className={`type-badge ${transaction.type}`}>
                  {transaction.type}
                </span>
              </span>

              <span className="remark-cell">
                <span className="remark-text" title={transaction.remark || "No remark"}>
                  {transaction.remark || "-"}
                </span>
              </span>

              <span className="date-cell">{formatDate(transaction.date)}</span>

              <span className="action-buttons">
                <button
                  onClick={() => handleOpenRepeatModal(transaction)}
                  className="transaction-repeat-btn"
                  title="Repeat transaction"
                >
                  <FaRedo /> <span className="btn-text">Repeat</span>
                </button>
                <button
                  onClick={() =>
                    deleteTransaction(transaction.walletId, transaction.id)
                  }
                  className="transaction-delete-btn"
                  title="Delete transaction"
                >
                  <FaTrash /> <span className="btn-text">Delete</span>
                </button>
              </span>
            </div>
          ))
        )}
      </div>
      </div>

      {/* Repeat Transaction Modal */}
      {repeatModalOpen && repeatTransaction_ && (
        <div className="repeat-modal-overlay" onClick={handleCancelRepeat}>
          <div className="repeat-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Repeat Transaction</h3>
            <div className="repeat-modal-content">
              <p><strong>Category:</strong> {repeatTransaction_.category}</p>
              <p><strong>Type:</strong> {repeatTransaction_.type}</p>
              <p><strong>Original Amount:</strong> ${repeatTransaction_.amount.toFixed(2)}</p>
              
              <div className="repeat-amount-input">
                <label htmlFor="repeatAmount">New Amount:</label>
                <input
                  type="number"
                  id="repeatAmount"
                  step="0.01"
                  min="0.01"
                  value={repeatAmount}
                  onChange={(e) => setRepeatAmount(e.target.value)}
                  className="repeat-amount-field"
                />
              </div>

              <div className="repeat-remark-input">
                <label htmlFor="repeatRemark">Remark:</label>
                <input
                  type="text"
                  id="repeatRemark"
                  value={repeatRemark}
                  onChange={(e) => setRepeatRemark(e.target.value)}
                  className="repeat-remark-field"
                  placeholder="Add or edit remark..."
                  maxLength="200"
                />
              </div>
            </div>
            <div className="repeat-modal-buttons">
              <button onClick={handleConfirmRepeat} className="repeat-confirm-btn">
                Confirm Repeat
              </button>
              <button onClick={handleCancelRepeat} className="repeat-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionList;
